import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Mail, Loader2, X } from 'lucide-react';

/**
 * Banner exibido quando o usuário está logado mas o e-mail não foi verificado.
 * Botão "Reenviar e-mail" chama o fluxo oficial de produção:
 * POST /api/auth/resend-verification — gera novo token e dispara o e-mail
 * via Resend (domínio verificado).
 */
export default function EmailVerificationBanner({ user, onVerified, onDismiss }) {
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [sent, setSent] = useState(false);

  // ==================== [FIX — auto-revalidação do email_verified] ====================
  // O AuthContext.user é populado apenas no mount inicial do App via fetchUser()
  // e via login(). Quando o usuário confirma o e-mail no NAVEGADOR (fora do app),
  // o backend grava email_verified=true no Mongo mas o React state permanece stale
  // com email_verified=false, fazendo o banner reaparecer indefinidamente.
  //
  // Este useEffect revalida silenciosamente contra GET /api/auth/me:
  //   1) Ao montar o banner (retorno do navegador -> Dashboard remonta -> banner monta).
  //   2) Quando o app volta a ficar visível (visibilitychange) — cobre warm-start
  //      do WebView do Capacitor Android, onde o processo JS não morre.
  //
  // Se o backend confirmar email_verified=true, dispara onVerified() (que já está
  // cabeado ao refreshUser() do AuthContext no Dashboard) e o banner some
  // naturalmente na próxima renderização via a guarda da linha 21.
  useEffect(() => {
    // Se já está verificado no props atual, não precisa revalidar.
    if (!user || user.email_verified) return;

    let cancelled = false;

    const revalidate = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data?.user?.email_verified === true) {
          if (onVerified) onVerified();
        }
      } catch (_) {
        // Silencioso — se falhar, o banner continua no estado atual (comportamento
        // anterior preservado). Nenhum toast, nenhum erro visível ao usuário.
      }
    };

    // Revalidar imediatamente ao montar.
    revalidate();

    // Revalidar quando o app volta a ficar visível (retorno do navegador externo,
    // troca de app, background/foreground do Capacitor Android).
    const onVisibility = () => {
      if (document.visibilityState === 'visible') revalidate();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, onVerified]);
  // ==================== [/FIX] ====================

  // Não mostrar se e-mail já verificado ou se foi dispensado
  if (user?.email_verified || dismissed) {
    return null;
  }

  const handleResend = async () => {
    if (!user?.email) {
      toast.error('E-mail do usuário não disponível.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: user.email });
      toast.success('E-mail de verificação reenviado. Verifique sua caixa de entrada.');
      setSent(true);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 429) {
        toast.error(detail || 'Aguarde alguns segundos antes de solicitar outro e-mail.');
      } else if (status === 400 && detail?.includes('já foi verificado')) {
        toast.success('E-mail já verificado. Faça login novamente.');
        if (onVerified) onVerified();
      } else {
        toast.error(detail || 'Erro ao reenviar e-mail de verificação.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <Alert className="mb-4 border-amber-200 bg-amber-50 relative overflow-hidden max-w-full" data-testid="email-verification-banner">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertDescription className="text-amber-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex-1 min-w-0 break-words">
          <strong>⚠️ E-mail não verificado</strong>
          <p className="text-sm mt-1 break-words">
            Seu e-mail ainda não foi verificado. Verifique sua caixa de entrada
            {user?.email ? ` (${user.email})` : ''} para confirmar.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 sm:ml-4">
          <Button
            size="sm"
            variant="default"
            onClick={handleResend}
            disabled={loading || sent}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="resend-verification-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                Enviando...
              </>
            ) : sent ? (
              <>
                <Mail className="w-3 h-3 mr-1" />
                E-mail enviado
              </>
            ) : (
              <>
                <Mail className="w-3 h-3 mr-1" />
                Reenviar e-mail
              </>
            )}
          </Button>

          <button
            onClick={handleDismiss}
            className="text-amber-600 hover:text-amber-800 p-1"
            title="Dispensar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
